import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { StyleSheet, Text, View, type PressableProps, type ViewStyle } from 'react-native'
import { LinearGradient } from 'expo-linear-gradient'
import { Pressable } from './Pressable'
import { AnimatedSpinner } from './AnimatedSpinner'
import { tintIconNode } from './tint-icon-node'
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  interpolateColor,
} from 'react-native-reanimated'
import { COLORS, FONTS } from '../../lib/brand'
import { INTERACTION, RADIUS, SPACING, useSemanticColors, DURATION } from '../../lib/theme/tokens'
import { useAnimatedPress } from '../../lib/hooks/use-animated-press'
import { ImpactStyle } from '../../lib/utils/haptics'
import { useTheme } from '../../lib/hooks/use-theme'
import { useFontScale } from '../../lib/hooks/use-font-scale'

type ButtonVariant = 'primary' | 'secondary' | 'text'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends Omit<PressableProps, 'children'> {
  title: string
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  icon?: ReactNode
  /**
   * Prevent rapid double taps from firing two submits/navigation pushes.
   * Enabled by default because nearly every Tago button is a command.
   */
  preventDoubleTap?: boolean
}

/** WCAG/MD3 minimum readable size — `adjustsFontSizeToFit` may not go below. */
const MIN_FONT_SIZE = 12

const SIZE_MAP: Record<
  ButtonSize,
  { height: number; paddingHorizontal: number; fontSize: number }
> = {
  sm: { height: INTERACTION.minTouchTarget, paddingHorizontal: SPACING.md, fontSize: 14 },
  md: { height: 48, paddingHorizontal: SPACING.lg, fontSize: 15 },
  lg: { height: 56, paddingHorizontal: SPACING.xl, fontSize: 16 },
}

export function Button({
  title,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  icon,
  preventDoubleTap = true,
  onPress,
  onPressIn: onPressInProp,
  onPressOut: onPressOutProp,
  style: styleProp,
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading
  // Disabled has ONE recipe in both themes. It used to be `opacity: 0.4` over
  // the coral fill, which is not a state — it is a filter, and a filter reads
  // differently depending on what is behind it: on a light background the
  // result was a pale pink slab with a near-white label (an enabled secondary
  // button), on a dark one a deep maroon slab with a grey label (a disabled
  // button). Same prop, two messages. Now the surface drops to the neutral
  // `surfaceSubtle` step and the label to `textDisabled`, both of which are
  // already theme-resolved, so the state looks identical either side.
  //
  // Loading is deliberately excluded: it locks the button but it is not off,
  // and the spinner is painted white, which only survives on the coral fill.
  const isInactive = Boolean(disabled) && !loading
  const tokens = SIZE_MAP[size]
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  // `SIZE_MAP` heights are raw dp. Applied as `height` they were a ceiling the
  // label could never push past, and `adjustsFontSizeToFit` below then shrank
  // the label to fit it — so asking Android for larger text made every button
  // label in the app smaller. See `useFontScale` for why that is the setting
  // ignored rather than honoured.
  const { box, enlarged } = useFontScale()
  const pressLockedRef = useRef(false)
  const releaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Circuit breaker for the async path — see `handlePress`. Mirrors
  // `lib/hooks/use-press-action.ts`, which has had one since it was written;
  // Button is the primitive that never got it.
  const circuitBreakerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Tactile weight follows visual weight. A primary button is the one action
  // the screen is asking for, so its tick is heavier than a secondary's — the
  // finger should be able to tell them apart without the eyes. `impact` is a
  // no-op on web, so this never fires in a browser.
  const {
    animatedStyle: scaleStyle,
    onPressIn: pressInAnim,
    onPressOut: pressOutAnim,
  } = useAnimatedPress({
    disabled: isDisabled,
    haptic: variant === 'primary' ? ImpactStyle.Medium : ImpactStyle.Light,
  })

  // Pressed background shift (primary only). The resting fill is coral.dark
  // for contrast, so the press reads as a lift to coral.DEFAULT rather than a
  // darken — a darker notch than coral.dark does not exist in the palette.
  const pressed = useSharedValue(0)
  const bgStyle = useAnimatedStyle(() => {
    if (variant !== 'primary') return {}
    return {
      backgroundColor: interpolateColor(
        pressed.value,
        [0, 1],
        [COLORS.coral.dark, COLORS.coral.DEFAULT],
      ),
    }
  })

  // Loading crossfade
  const loadingProgress = useSharedValue(loading ? 1 : 0)

  useEffect(() => {
    loadingProgress.value = withTiming(loading ? 1 : 0, { duration: DURATION.fast })
  }, [loading, loadingProgress])

  useEffect(() => {
    return () => {
      if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current)
      if (circuitBreakerRef.current) clearTimeout(circuitBreakerRef.current)
    }
  }, [])

  const contentOpacity = useAnimatedStyle(() => ({
    opacity: 1 - loadingProgress.value,
  }))

  const spinnerOpacity = useAnimatedStyle(() => ({
    opacity: loadingProgress.value,
  }))

  const handlePressIn = (e: Parameters<NonNullable<PressableProps['onPressIn']>>[0]) => {
    pressed.value = withTiming(1, { duration: DURATION.instant })
    pressInAnim()
    onPressInProp?.(e)
  }

  const handlePressOut = (e: Parameters<NonNullable<PressableProps['onPressOut']>>[0]) => {
    pressed.value = withTiming(0, { duration: DURATION.fast })
    pressOutAnim()
    onPressOutProp?.(e)
  }

  const clearLockTimers = useCallback(() => {
    if (releaseTimerRef.current) clearTimeout(releaseTimerRef.current)
    if (circuitBreakerRef.current) clearTimeout(circuitBreakerRef.current)
    releaseTimerRef.current = null
    circuitBreakerRef.current = null
  }, [])

  const releasePressLock = useCallback(() => {
    clearLockTimers()
    releaseTimerRef.current = setTimeout(() => {
      pressLockedRef.current = false
      releaseTimerRef.current = null
    }, INTERACTION.pressLockMs)
  }, [clearLockTimers])

  /** Unlock immediately, no debounce — used by the circuit breaker. */
  const forceUnlock = useCallback(() => {
    clearLockTimers()
    pressLockedRef.current = false
  }, [clearLockTimers])

  const handlePress = useCallback(
    (event: Parameters<NonNullable<PressableProps['onPress']>>[0]) => {
      if (isDisabled) return
      if (preventDoubleTap && pressLockedRef.current) return

      pressLockedRef.current = true

      let result: unknown
      try {
        result = onPress?.(event) as unknown
      } catch (error) {
        releasePressLock()
        throw error
      }

      if (result && typeof (result as Promise<unknown>).finally === 'function') {
        // `preventDoubleTap` defaults to true and is never overridden anywhere,
        // so this lock applies to every Button in the app. Releasing it ONLY on
        // promise settlement meant an `onPress` whose promise never settles —
        // a fetch with no timeout, a native call that never returns — killed
        // that button for the rest of its mounted life. Invisibly: it is not
        // `disabled`, shows no spinner, and press-in/press-out still animate,
        // so the button visibly reacts to every tap and does nothing. That is
        // the owner's report with no overlay involved.
        //
        // The breaker bounds the lock. It cannot cause a double submit that the
        // settled path would have prevented: by the time it fires, the debounce
        // window is long gone either way.
        circuitBreakerRef.current = setTimeout(forceUnlock, INTERACTION.maxAsyncPressLockMs)
        void (result as Promise<unknown>).finally(releasePressLock)
      } else {
        releasePressLock()
      }
    },
    [forceUnlock, isDisabled, onPress, preventDoubleTap, releasePressLock],
  )

  const accentColor = isDark ? colors.primaryLight : colors.primary
  const activeTextColor = variant === 'primary' ? '#FFFFFF' : accentColor
  const textColor = isInactive ? colors.textDisabled : activeTextColor
  const spinnerColor = variant === 'primary' ? '#FFFFFF' : accentColor
  const resolvedIcon = useMemo(
    () => (icon ? tintIconNode(icon, textColor) : null),
    [icon, textColor],
  )

  const containerStyle = useMemo<ViewStyle>(() => {
    const base: ViewStyle = {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      // `minHeight`, not `height`: at scale 1 the two are identical (the label
      // line box is well under 44/48/56), but a fixed height cannot let a
      // two-line label out. `box()` moves the floor with the text so the
      // button still reads as a button rather than as text with a stripe.
      minHeight: box(tokens.height),
      minWidth: INTERACTION.minTouchTarget,
      paddingHorizontal: tokens.paddingHorizontal,
      paddingVertical: enlarged ? SPACING.sm : 0,
      borderRadius: RADIUS.lg,
      // The gradient and the animated fill live inside the press target. The
      // target, not a stretched wrapper, must own the clip so a compact CTA
      // cannot leave a larger coloured rectangle behind it.
      overflow: 'hidden',
    }

    if (isInactive) {
      // The neutral off-state, derived from the same two tokens in both
      // themes. `primary` keeps a filled silhouette so the layout does not
      // shift; `secondary` keeps its outline; `text` stays chromeless.
      if (variant === 'primary') {
        base.backgroundColor = colors.surfaceSubtle
        base.borderWidth = 1
        base.borderColor = colors.border
      } else if (variant === 'secondary') {
        base.borderWidth = 1.5
        base.borderColor = colors.border
        base.backgroundColor = 'transparent'
      } else {
        base.backgroundColor = 'transparent'
      }
    } else if (variant === 'primary') {
      // White on coral.DEFAULT is 3.80:1 — below WCAG AA for body-size text,
      // and this is the fill under every CTA in the app. coral.dark measures
      // 5.50:1 (recomputed — an earlier note here claimed 5.8, which was wrong).
      //
      // The rule this sets, so the two reds do not look like an accident:
      // coral.dark wherever white text sits ON the fill; coral.DEFAULT stays for
      // decorative coral that carries no text — progress fills, dots, badges.
      // That is why those were deliberately not swept with this. coral.dark keeps the
      // brand and takes white to 5.8:1.
      base.backgroundColor = COLORS.coral.dark
      // A primary action is the only consistently saturated object on a
      // screen. This narrow luminous edge makes it feel like a tactile glass
      // control instead of a flat red rectangle, without weakening the white
      // label's contrast.
      base.borderWidth = 1
      base.borderColor = isDark ? 'rgba(255,255,255,0.20)' : 'rgba(255,255,255,0.34)'
    } else if (variant === 'secondary') {
      base.borderWidth = 1.5
      base.borderColor = isDark ? 'rgba(255,106,82,0.42)' : 'rgba(240,66,45,0.38)'
      base.backgroundColor = isDark ? 'rgba(255,106,82,0.10)' : 'rgba(240,66,45,0.06)'
    } else {
      base.backgroundColor = 'transparent'
    }

    return base
  }, [variant, tokens, isInactive, isDark, colors.surfaceSubtle, colors.border, box, enlarged])

  // `style` belongs to the Pressable because it is part of the public Button
  // API. The animation wrapper still needs the explicit sizing constraints,
  // otherwise React Native stretches that wrapper to its column while the
  // Pressable honours `alignSelf: 'flex-start'` (the Services CTA used to
  // render as a small button sitting inside a full-width red slab).
  //
  // Only static layout constraints are mirrored. Visual properties, margins
  // and press-state styles remain on the real touch target and are never
  // applied twice.
  const wrapperLayoutStyle = useMemo<ViewStyle>(() => {
    if (typeof styleProp === 'function') return {}
    const style = StyleSheet.flatten(styleProp)
    if (!style) return {}

    return {
      alignSelf: style.alignSelf,
      width: style.width,
      minWidth: style.minWidth,
      maxWidth: style.maxWidth,
      flex: style.flex,
      flexGrow: style.flexGrow,
      flexShrink: style.flexShrink,
    }
  }, [styleProp])

  return (
    <Animated.View style={[scaleStyle, wrapperLayoutStyle]} collapsable={false}>
      <Pressable
        disabled={isDisabled}
        style={
          typeof styleProp === 'function'
            ? (state) => [containerStyle, styleProp(state)]
            : [containerStyle, styleProp]
        }
        onPress={handlePress}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        accessibilityRole="button"
        accessibilityLabel={title}
        accessibilityState={{ disabled: isDisabled, busy: loading }}
        {...props}
      >
        {variant === 'primary' && !isInactive ? (
          <Animated.View
            pointerEvents="none"
            style={[StyleSheet.absoluteFill, { borderRadius: RADIUS.lg }, bgStyle]}
          />
        ) : null}
        {variant === 'primary' && !isInactive ? (
          <LinearGradient
            pointerEvents="none"
            colors={['rgba(255,255,255,0.20)', 'rgba(255,255,255,0.035)', 'rgba(255,255,255,0)']}
            locations={[0, 0.28, 0.78]}
            start={{ x: 0.12, y: 0 }}
            end={{ x: 0.92, y: 1 }}
            style={[StyleSheet.absoluteFill, { borderRadius: RADIUS.lg }]}
          />
        ) : null}
        <Animated.View
          style={[
            { position: 'absolute', alignItems: 'center', justifyContent: 'center' },
            spinnerOpacity,
          ]}
        >
          <AnimatedSpinner size={18} centered={false} color={spinnerColor} />
        </Animated.View>
        <Animated.View
          style={[
            {
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 0,
              maxWidth: '100%',
            },
            contentOpacity,
          ]}
        >
          {resolvedIcon ? (
            <View style={{ marginRight: SPACING.xs, flexShrink: 0 }}>{resolvedIcon}</View>
          ) : null}
          <Text
            style={{
              fontSize: tokens.fontSize,
              fontFamily: FONTS.bodyBold,
              color: textColor,
              textAlign: 'center',
              flexShrink: 1,
              maxWidth: '100%',
            }}
            // Shrink-to-fit is the right answer for exactly one reader: the
            // one who never touched their text size. For them nothing here
            // changes — one line, floored at 12px per size (sm 0.857, md
            // 0.80, lg 0.75) rather than one global 0.78, which took sm down
            // to 10.9px.
            //
            // For everyone else it is the setting undone: the whole point of
            // `font_scale 2.0` is that 15pt should render at ~27, and this
            // prop took it back to 12. Above the baseline the label keeps
            // the size it was asked for and the button grows instead — two
            // lines if the label needs two.
            numberOfLines={enlarged ? 2 : 1}
            adjustsFontSizeToFit={!enlarged}
            minimumFontScale={enlarged ? undefined : MIN_FONT_SIZE / tokens.fontSize}
          >
            {title}
          </Text>
        </Animated.View>
      </Pressable>
    </Animated.View>
  )
}
